import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { inboxKindActionsApi } from '../api/inbox-kind-actions.ts';
import { useInboxKindScope } from '../state/inbox-kind-scope.ts';
import { InboxKindActionCard } from '../components/inbox-kind-suggestions.tsx';
import { Alert, Button, LoadingBlock } from '../components/ui.tsx';
import type { ReactNode } from 'react';
import { WorkPage, WorkSection } from '../components/work-page.tsx';
import '../components/messages-presentation.css';

/** Old servers retain their existing confirmation page; kinds are advertised explicitly. */
export function InboxKindApprovalSwitch({ children, framed = false }: { children: ReactNode; framed?: boolean }) {
  const scope = useInboxKindScope('confirmation-dispatch');
  return scope.kinds.some((kind) => kind !== 'reschedule') ? <InboxKindApprovalPage framed={framed} /> : children;
}

/** Authenticated confirmation fetches are read only; only the human decision button performs a POST. */
export default function InboxKindApprovalPage({ framed = false }: { framed?: boolean } = {}) {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const { t } = useTranslation();
  const scope = useInboxKindScope(`${id}:${token}`);
  const content =
    !scope.allowed || !scope.kinds.some(scope.canRead) ? (
      <Alert tone="error">{t('inboxAction.linkError')}</Alert>
    ) : (
      <Approval
        key={scope.identity}
        id={id}
        token={token}
        identity={scope.identity}
        scopeCurrent={scope.current}
        receiptScopeCurrent={scope.receiptCurrent}
        canRead={scope.canRead}
        canDecide={scope.canDecide}
      />
    );
  return framed ? (
    content
  ) : (
    <div className="messages-page messages-approval">
      <WorkPage title={t('inboxAction.confirm')} description={t('inboxKind.confirmReview')}>
        <nav aria-label={t('nav.messages')}>
          <Link to="/messages" className="text-[color:var(--color-brand-strong)] underline underline-offset-4">
            {t('nav.messages')}
          </Link>
        </nav>
        {content}
      </WorkPage>
    </div>
  );
}

function Approval({
  id,
  token,
  identity,
  scopeCurrent,
  receiptScopeCurrent,
  canRead,
  canDecide,
}: {
  id: string;
  token: string;
  identity: string;
  scopeCurrent: () => boolean;
  receiptScopeCurrent: () => boolean;
  canRead: ReturnType<typeof useInboxKindScope>['canRead'];
  canDecide: ReturnType<typeof useInboxKindScope>['canDecide'];
}) {
  const { t } = useTranslation();
  const key = ['inbox-kind-approval', id, token, identity];
  const query = useQuery({ queryKey: key, queryFn: () => inboxKindActionsApi.confirm(id, token), retry: false });
  const suggestion = query.data?.suggestion;
  return (
    <WorkSection title={t('messagesInbox.reviewSection')} description={t('inboxKind.confirmReview')}>
      {query.isPending ? (
        <LoadingBlock label={t('common.loading')} />
      ) : query.error || !suggestion || !canRead(suggestion.proposal.kind) ? (
        <Alert tone="error">
          {t('inboxAction.linkError')}{' '}
          <Button variant="secondary" onClick={() => void query.refetch()}>
            {t('common.retry')}
          </Button>
        </Alert>
      ) : (
        <InboxKindActionCard
          key={`${suggestion.id}:${JSON.stringify(suggestion)}`}
          suggestion={suggestion}
          canDecide={canDecide(suggestion.proposal.kind) && !query.isFetching}
          queryKey={key}
          queryData={query.data}
          scopeCurrent={scopeCurrent}
          receiptScopeCurrent={receiptScopeCurrent}
          token={token}
          refresh={() => query.refetch()}
        />
      )}
    </WorkSection>
  );
}
